import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

interface ExpiryRingProps {
  issueDate?: string | null;
  expiryDate: string;
  size?: number;
}

// The one "time left until expiry" ring, shared by the certificate rows and the reminder rows —
// same formula, same colors, everywhere a countdown-to-expiry is shown.
//
// Fill = % of the certificate's OWN lifespan still remaining (expiry - issue = the full window,
// e.g. 365 days for a 1-year cert), not a generic/arbitrary window — so a ring reads the same way
// regardless of whether the cert renews annually or every 10 years. Full circle = far from expiry,
// draining away as the deadline approaches (the "fuel gauge" convention), lands on the day count
// used to determine daysLeft.
const ExpiryRing: React.FC<ExpiryRingProps> = ({ issueDate, expiryDate, size = 46 }) => {
  const { palette } = useTheme();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  const daysLeft = Math.ceil((expiry.getTime() - today.getTime()) / 86400000);

  // Certs without a recorded issue date (data-quality gap) fall back to a standard 1-year window
  // rather than crashing or showing a meaningless ring.
  let totalDays = 365;
  if (issueDate) {
    const issue = new Date(issueDate);
    if (!isNaN(issue.getTime())) {
      totalDays = Math.max(1, Math.round((expiry.getTime() - issue.getTime()) / 86400000));
    }
  }

  const percentLeft = Math.min(100, Math.max(0, (daysLeft / totalDays) * 100));
  const expired = daysLeft < 0;
  const color = expired || percentLeft < 10 ? palette.tokens.error : percentLeft <= 50 ? palette.tokens.warning : palette.tokens.success;

  // Geometry is computed against a fixed base size and the SVG scales via viewBox + percentage
  // width/height — so the outer Box's CSS width/height is a real, transitionable property (same
  // trick as the thumbnail's zoom), and the ring scales fluidly with it instead of jumping (SVG
  // attributes like a raw width= don't animate; CSS width on a percentage-sized SVG does).
  const base = 46;
  const strokeWidth = 4;
  const radius = (base - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percentLeft / 100);
  const scale = size / base;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, transition: 'width 0.25s ease, height 0.25s ease' }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${base} ${base}`}>
        <circle cx={base / 2} cy={base / 2} r={radius} fill="none" stroke={palette.tokens.hairlineSoft} strokeWidth={strokeWidth} />
        <circle
          cx={base / 2} cy={base / 2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={expired ? circumference : offset}
          transform={`rotate(-90 ${base / 2} ${base / 2})`}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: `${(size < 44 ? 0.6 : 0.68) * scale}rem`, fontWeight: 800, color: expired ? palette.tokens.error : palette.tokens.ink, lineHeight: 1.1 }}>
          {expired ? 'Expired' : `${daysLeft}d`}
        </Typography>
        {!expired && (
          <Typography sx={{ fontSize: `${0.5 * scale}rem`, color: palette.tokens.mutedSoft, lineHeight: 1 }}>left</Typography>
        )}
      </Box>
    </Box>
  );
};

export default ExpiryRing;
